import { useState } from "react";
import { cn } from "@/lib/cn";

/**
 * Shows a message body the way its recipient sees it.
 *
 * <p>Outbound mail carries an HTML alternative — that is where the compliance
 * footer's "unsubscribe here" is a real link — so when one is present it is
 * rendered in place of the text part. It renders inside a sandboxed frame,
 * never the page DOM: nothing in it can run a script, reach the network (the
 * frame's own CSP is {@code default-src 'none'}) or open a window — every link
 * targets a new window and the sandbox refuses to open one. That last point is
 * deliberate: the unsubscribe link is live and a GET on it opts the prospect
 * out, so an operator must not be able to trip it from a preview.
 *
 * <p>Messages without HTML — inbound mail, drafts still under review — fall
 * back to the text part.
 */
export default function EmailBodyView({
  bodyText,
  bodyHtml,
  className,
}: {
  bodyText: string;
  bodyHtml: string | null;
  className?: string;
}) {
  const [height, setHeight] = useState<number>(MIN_HEIGHT_PX);
  const html = bodyHtml?.trim();

  if (!html) {
    return (
      <pre className={cn("whitespace-pre-wrap font-sans text-sm", className)}>{bodyText}</pre>
    );
  }
  return (
    <iframe
      title="Message body"
      sandbox="allow-same-origin"
      srcDoc={frameDocument(html)}
      onLoad={(e) => setHeight(contentHeight(e.currentTarget))}
      className={cn("block w-full border-0", className)}
      style={{ height }}
    />
  );
}

const MIN_HEIGHT_PX = 48;

/**
 * `allow-same-origin` (with scripts still forbidden) is what lets the parent
 * read the frame's document to size it to its content.
 */
function contentHeight(frame: HTMLIFrameElement): number {
  const measured = frame.contentDocument?.documentElement?.scrollHeight ?? 0;
  return Math.max(MIN_HEIGHT_PX, measured);
}

const FRAME_STYLE =
  "html{overflow:hidden}html,body{margin:0;padding:0}" +
  "body{font:14px/1.5 system-ui,-apple-system,sans-serif;color:#27272a;overflow-wrap:anywhere}" +
  "p{margin:0 0 1em}p:last-child{margin-bottom:0}a{color:#2563eb}img{max-width:100%}";

function frameDocument(html: string): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    "<meta http-equiv=\"Content-Security-Policy\" content=\"default-src 'none'; style-src 'unsafe-inline'; img-src data:\">" +
    '<base target="_blank">' +
    `<style>${FRAME_STYLE}</style></head><body>${html}</body></html>`
  );
}
